import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

import {SectionHeader} from 'UI/components/layout/SectionHeader';
import {WalkCard} from 'UI/components/modules/WalkCard';

import {useFeaturedWalksBlock} from './useFeaturedWalksBlock';

import type {Walk} from 'types/payload-types';

/**
 * Props for the FeaturedWalksBlock component.
 */
interface ComponentProps {
    /** Label for the "View All" action link. */
    actionLabel?: string | null;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     *
     * @default 'FeaturedWalksBlock'
     */
    testId?: string;
    /** Section title displayed above the horizontal scroll. */
    title: string;
    /** Array of populated Walk documents to display. */
    walks?: (Walk | string)[] | null;
}

/**
 * Renders a slider section of featured walk cards with dot pagination.
 * Uses keen-slider for touch/swipe support.
 *
 * @param props             The component props.
 * @param props.actionLabel Optional label for a "view all" action.
 * @param props.testId      An optional test ID. Defaults to `'FeaturedWalksBlock'`.
 * @param props.title       The section title.
 * @param props.walks       Array of Walk documents to display.
 * @returns A JSX element rendering the featured walks slider.
 *
 * @example
 * ```tsx
 * <FeaturedWalksBlock title="Featured in Augsburg" walks={walks} />
 * ```
 */
const FeaturedWalksBlock = ({actionLabel, testId = 'FeaturedWalksBlock', title, walks}: ComponentProps) => {
    const {activeIndex, canScroll, populatedWalks, sliderRef} = useFeaturedWalksBlock(walks);

    if (populatedWalks.length === 0) return null;

    return (
        <Section data-cy={testId}>
            <SectionHeader actionLabel={actionLabel ?? undefined} title={title} />
            <SliderWrapper>
                <Track ref={sliderRef} className="keen-slider">
                    {populatedWalks.map(walk => (
                        <Slide key={walk.id} className="keen-slider__slide">
                            <WalkCard walk={walk} />
                        </Slide>
                    ))}
                </Track>
                {canScroll && (
                    <Dots data-cy={`${testId}-dots`}>
                        {populatedWalks.map((walk, index) => (
                            <Dot key={walk.id} $isActive={index === activeIndex} />
                        ))}
                    </Dots>
                )}
            </SliderWrapper>
        </Section>
    );
};

FeaturedWalksBlock.displayName = 'FeaturedWalksBlock';

export {FeaturedWalksBlock};

// ── Styled ──────────────────────────────────────────────────

const Section = styled.section`
    display: flex;
    flex-direction: column;
    gap: ${spacing(6)};
`;

const SliderWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(4)};
`;

const Track = styled.div`
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
    display: flex;
    overflow: hidden;
    touch-action: pan-y;
    user-select: none;
`;

const Slide = styled.div`
    min-width: 0;
    overflow: hidden;

    a {
        color: ${({theme}) => theme.colors.white};
    }
`;

const Dots = styled.div`
    align-items: center;
    display: flex;
    gap: ${spacing(2)};
    justify-content: center;
`;

interface DotProps {
    $isActive: boolean;
}

const Dot = styled.div<DotProps>`
    background: ${({$isActive, theme}) => ($isActive ? theme.colors.primaryDefault : theme.colors.surfaceHigh)};
    border-radius: 50%;
    border-radius: ${({$isActive}) => ($isActive ? '0.4rem' : '50%')};
    height: 0.8rem;
    transition: background 0.2s ease;
    width: 0.8rem;
`;